import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, FlatList, Image, useWindowDimensions } from 'react-native';
import { Card, Title, Paragraph, Button, FAB, Dialog, Portal, TextInput, Snackbar, ActivityIndicator, useTheme, IconButton, Divider, Switch, SegmentedButtons } from 'react-native-paper';
import { supabase } from '../../services/supabase';
import { evolutionApiService, QRCodeResponse } from '../../services/evolutionApiService';

type Props = {
  navigation: any;
};

type InfraestruturaChatbot = {
  id?: number;
  rotaapi: string;
  codempresa: number;
  tipo_integracao: string;
  api_whatsapp: any;
  apikey_instance: string;
  instance_name: string;
  numero_whatsapp?: string;
};

type NovaInstanciaEvolution = {
  instanceName: string;
  numero: string;
  codempresa: number;
};

export function GerenciarInfraestruturaChatbot({ navigation }: Props) {
  const theme = useTheme();
  const { width } = useWindowDimensions();
  const [loading, setLoading] = useState(false);
  const [infraestruturas, setInfraestruturas] = useState<InfraestruturaChatbot[]>([]);
  const [empresas, setEmpresas] = useState<{id: number, nome: string}[]>([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [currentInfraestrutura, setCurrentInfraestrutura] = useState<InfraestruturaChatbot>({
    rotaapi: '',
    codempresa: 0,
    tipo_integracao: 'smartcobranca',
    api_whatsapp: {},
    apikey_instance: '',
    instance_name: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [tipoIntegracaoFiltro, setTipoIntegracaoFiltro] = useState<string>('smartcobranca');
  const [evolutionDialogVisible, setEvolutionDialogVisible] = useState(false);
  const [novaInstancia, setNovaInstancia] = useState<NovaInstanciaEvolution>({
    instanceName: '',
    numero: '',
    codempresa: 0
  });
  const [creatingInstance, setCreatingInstance] = useState(false);
  const [qrCodeDialogVisible, setQRCodeDialogVisible] = useState(false);
  const [qrCodeData, setQRCodeData] = useState<QRCodeResponse | null>(null);
  const [loadingQRCode, setLoadingQRCode] = useState(false);
  const [currentInstanceName, setCurrentInstanceName] = useState('');
  const [statusInstancias, setStatusInstancias] = useState<Record<string, string>>({});

  const loadInfraestruturas = React.useCallback(async () => {
    try {
      setLoading(true);
      
      // Verificar se o usuário é admin
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data: userData, error: userError } = await supabase
        .from('usuarios')
        .select('admin, empresa')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;
      if (!userData?.admin) throw new Error('Acesso negado. Você não tem permissão de administrador.');

      // Buscar infraestruturas com filtro de tipo_integracao
      let query = supabase
        .from('infraestrutura_chatbot')
        .select('*');
        
      // Aplicar filtro apenas se tipoIntegracaoFiltro não for 'todos'
      if (tipoIntegracaoFiltro !== 'todos') {
        query = query.eq('tipo_integracao', tipoIntegracaoFiltro);
      }
      
      const { data, error } = await query;

      if (error) {
        console.error('Erro ao buscar infraestruturas:', error);
        throw error;
      }
      
      console.log(`Infraestruturas carregadas (${tipoIntegracaoFiltro}):`, data?.length || 0);
      setInfraestruturas(data || []);
      
      // Buscar status de cada instância
      if (data && data.length > 0) {
        await buscarStatusInstancias(data);
      }
    } catch (err: any) {
      console.error('Erro completo:', err);
      setError(err.message || 'Erro ao carregar infraestruturas');
    } finally {
      setLoading(false);
    }
  }, [tipoIntegracaoFiltro]);
  
  // Efeito para carregar dados iniciais
  useEffect(() => {
    loadInfraestruturas();
    loadEmpresas();
  }, [loadInfraestruturas]);

  const loadEmpresas = async () => {
    try {
      const { data, error } = await supabase
        .from('empresa')
        .select('id, nome')
        .order('nome', { ascending: true });

      if (error) throw error;
      
      setEmpresas(data || []);
    } catch (err: any) {
      console.error('Erro ao carregar empresas:', err);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      if (!currentInfraestrutura.rotaapi || !currentInfraestrutura.codempresa || !currentInfraestrutura.instance_name) {
        setError('Preencha todos os campos obrigatórios');
        setLoading(false);
        return;
      }

      // Obter o ID da empresa do usuário atual
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data: userData, error: userError } = await supabase
        .from('usuarios')
        .select('empresa, admin')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;
      if (!userData?.admin) throw new Error('Acesso negado. Você não tem permissão de administrador.');

      // Preparar objeto para salvar
      const infraestruturaParaSalvar = {
        rotaapi: currentInfraestrutura.rotaapi,
        codempresa: currentInfraestrutura.codempresa,
        tipo_integracao: currentInfraestrutura.tipo_integracao || 'smartcobranca',
        api_whatsapp: currentInfraestrutura.api_whatsapp || {},
        apikey_instance: currentInfraestrutura.apikey_instance,
        instance_name: currentInfraestrutura.instance_name
      };

      let result;

      if (isEditing && currentInfraestrutura.id) {
        // Atualizar infraestrutura existente
        result = await supabase
          .from('infraestrutura_chatbot')
          .update(infraestruturaParaSalvar)
          .eq('id', currentInfraestrutura.id);

        if (result.error) {
          console.error('Erro ao atualizar infraestrutura:', result.error);
          throw result.error;
        }
        
        setSuccess('Infraestrutura atualizada com sucesso!');
        console.log('Infraestrutura atualizada com sucesso!');
      } else {
        // Criar nova infraestrutura
        result = await supabase
          .from('infraestrutura_chatbot')
          .insert(infraestruturaParaSalvar);

        if (result.error) {
          console.error('Erro ao criar infraestrutura:', result.error);
          throw result.error;
        }
        
        setSuccess('Infraestrutura criada com sucesso!');
        console.log('Infraestrutura criada com sucesso!');
      }

      setDialogVisible(false);
      loadInfraestruturas();
    } catch (err: any) {
      console.error('Erro completo ao salvar:', err);
      setError(err.message || 'Erro ao salvar infraestrutura');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      
      if (!currentInfraestrutura.id) {
        setError('ID da infraestrutura não encontrado');
        setLoading(false);
        return;
      }

      const { error } = await supabase
        .from('infraestrutura_chatbot')
        .delete()
        .eq('id', currentInfraestrutura.id);

      if (error) throw error;
      
      setSuccess('Infraestrutura excluída com sucesso!');
      setDeleteDialogVisible(false);
      loadInfraestruturas();
    } catch (err: any) {
      setError(err.message || 'Erro ao excluir infraestrutura');
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setCurrentInfraestrutura({
      rotaapi: '',
      codempresa: 0,
      tipo_integracao: 'smartcobranca',
      api_whatsapp: {},
      apikey_instance: '',
      instance_name: ''
    });
    setIsEditing(false);
    setDialogVisible(true);
  };

  const openEditDialog = (infraestrutura: InfraestruturaChatbot) => {
    setCurrentInfraestrutura(infraestrutura);
    setIsEditing(true);
    setDialogVisible(true);
  };

  const openDeleteDialog = (infraestrutura: InfraestruturaChatbot) => {
    setCurrentInfraestrutura(infraestrutura);
    setDeleteDialogVisible(true);
  };

  const openEvolutionDialog = () => {
    setNovaInstancia({
      instanceName: '',
      numero: '',
      codempresa: empresas.length > 0 ? empresas[0].id : 0
    });
    setEvolutionDialogVisible(true);
  };

  const handleCreateEvolutionInstance = async () => {
    try {
      setCreatingInstance(true);
      
      if (!novaInstancia.instanceName || !novaInstancia.numero || !novaInstancia.codempresa) {
        setError('Preencha todos os campos obrigatórios');
        setCreatingInstance(false);
        return;
      }

      // Criar instância na Evolution API
      const result = await evolutionApiService.criarInstancia({
        instanceName: novaInstancia.instanceName,
        numero: novaInstancia.numero,
        codempresa: novaInstancia.codempresa
      });

      if (result.success) {
        setSuccess(result.message);
        setEvolutionDialogVisible(false);
        loadInfraestruturas(); // Recarregar a lista após criar a instância
      } else {
        setError(result.message);
      }
    } catch (err: any) {
      console.error('Erro ao criar instância:', err);
      setError(err.message || 'Erro ao criar instância');
    } finally {
      setCreatingInstance(false);
    }
  };
  
  const buscarStatusInstancias = async (instancias: InfraestruturaChatbot[]) => {
    try {
      const statusMap: Record<string, string> = {};
      const instanciasAtualizadas: InfraestruturaChatbot[] = [];
      
      console.log('\n=== Buscando status das instâncias ===');
      
      const detalhesPromises = instancias.map(async (inst) => {
        try {
          const detalhes = await evolutionApiService.buscarDetalhesInstancia(inst.instance_name);
          
          if (detalhes && detalhes.notFound) {
            statusMap[inst.instance_name] = 'not_registered';
            instanciasAtualizadas.push({
              ...inst,
              api_whatsapp: { ...inst.api_whatsapp, status: 'not_registered' }
            });
          } else if (detalhes) {
            statusMap[inst.instance_name] = detalhes.connectionStatus || detalhes.state || 'close';
            instanciasAtualizadas.push({
              ...inst,
              api_whatsapp: {
                ...inst.api_whatsapp,
                profileName: detalhes.profileName,
                profilePictureUrl: detalhes.profilePicUrl || detalhes.profilePictureUrl,
                status: detalhes.connectionStatus || detalhes.state,
                owner: detalhes.ownerJid
              }
            });
          } else {
            statusMap[inst.instance_name] = 'unknown';
            instanciasAtualizadas.push(inst);
          }
        } catch (err) {
          statusMap[inst.instance_name] = 'unknown';
          instanciasAtualizadas.push(inst);
        }
      });
      
      await Promise.all(detalhesPromises);
      setStatusInstancias(statusMap);
      
      if (instanciasAtualizadas.length > 0) {
        setInfraestruturas(instanciasAtualizadas);
      }
    } catch (err) {
      console.error('Erro ao buscar status:', err);
    }
  };

  const handleGetQRCode = async (instanceName: string) => {
    try {
      setLoadingQRCode(true);
      setCurrentInstanceName(instanceName);
      setQRCodeDialogVisible(true);
      
      const response = await evolutionApiService.obterQRCode(instanceName);
      
      if (!response) {
        setError('Erro ao obter QR code. Verifique se a instância existe.');
        return;
      }
      
      setQRCodeData(response);
      setSuccess('QR code obtido com sucesso');
    } catch (error: any) {
      console.error('Erro ao obter QR code:', error);
      setError(`Erro: ${error.message || 'Falha ao obter QR code'}`);
    } finally {
      setLoadingQRCode(false);
    }
  };

  // Calcular número de colunas baseado na largura
  const getNumColumns = () => {
    if (width >= 1200) return 3;
    if (width >= 768) return 2;
    return 1;
  };

  const numColumns = getNumColumns();

  const renderItem = ({ item }: { item: InfraestruturaChatbot }) => {
    const empresa = empresas.find(e => e.id === item.codempresa);
    const status = statusInstancias[item.instance_name] || 'unknown';
    const isConnected = status === 'open';
    
    const profileName = item.api_whatsapp?.profileName || item.instance_name;
    const profilePictureUrl = item.api_whatsapp?.profilePictureUrl;
    
    let numeroFormatado = 'Não informado';
    if (item.api_whatsapp?.owner) {
      const numeroMatch = item.api_whatsapp.owner.match(/^(\d+)@/);
      if (numeroMatch) {
        numeroFormatado = numeroMatch[1];
      }
    } else if (item.numero_whatsapp) {
      numeroFormatado = item.numero_whatsapp;
    }
    
    return (
      <Card style={styles.modernCard} elevation={2}>
        <Card.Content style={styles.modernCardContent}>
          {/* Header */}
          <View style={styles.modernHeader}>
            <View style={styles.headerLeft}>
              <Title style={styles.instanceTitle}>{item.instance_name}</Title>
              <View style={styles.badge}>
                <Paragraph style={styles.badgeText}>{item.tipo_integracao}</Paragraph>
              </View>
            </View>
          </View>
          
          <Divider style={styles.modernDivider} />
          
          {/* Perfil */}
          <View style={styles.profileSection}>
            <View style={styles.avatarContainer}>
              {profilePictureUrl ? (
                <Image
                  source={{ uri: profilePictureUrl }}
                  style={styles.avatar}
                  onError={(e) => console.log('Erro ao carregar imagem:', e.nativeEvent.error)}
                />
              ) : (
                <View style={styles.avatarPlaceholder}>
                  <IconButton icon="account" size={20} iconColor="#999" style={{margin: 0}} />
                </View>
              )}
            </View>
            
            <View style={styles.profileInfo}>
              <Title style={styles.profileName}>{profileName}</Title>
              <Paragraph style={styles.profileNumber}>{numeroFormatado}</Paragraph>
              <Paragraph style={styles.empresaText}>{empresa?.nome || 'N/A'}</Paragraph>
            </View>
          </View>
          
          {/* Status */}
          <View style={styles.statusSection}>
            {status === 'not_registered' ? (
              <View style={styles.notRegisteredBadge}>
                <IconButton icon="alert-circle" size={14} iconColor="#fff" style={styles.statusIcon} />
                <Paragraph style={styles.statusText}>Não Cadastrada</Paragraph>
              </View>
            ) : isConnected ? (
              <View style={styles.connectedBadge}>
                <IconButton icon="check-circle" size={14} iconColor="#fff" style={styles.statusIcon} />
                <Paragraph style={styles.statusText}>Conectado</Paragraph>
              </View>
            ) : (
              <View style={styles.disconnectedBadge}>
                <IconButton icon="close-circle" size={14} iconColor="#fff" style={styles.statusIcon} />
                <Paragraph style={styles.statusText}>Desconectado</Paragraph>
              </View>
            )}
          </View>
          
          {/* Ações */}
          <View style={styles.actionsRow}>
            <Button
              mode="contained"
              icon="qrcode"
              onPress={() => handleGetQRCode(item.instance_name)}
              style={styles.actionButton}
              labelStyle={styles.actionButtonLabel}
              buttonColor="#25D366"
              compact
            >
              QR Code
            </Button>
            <IconButton
              icon="pencil"
              size={20}
              onPress={() => openEditDialog(item)}
              iconColor="#2196F3"
              style={styles.iconButton}
            />
            <IconButton
              icon="delete"
              size={20}
              onPress={() => openDeleteDialog(item)}
              iconColor="#DC3545"
              style={styles.iconButton}
            />
          </View>
        </Card.Content>
      </Card>
    );
  };

  if (loading && infraestruturas.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Title style={styles.title}>Gerenciar Infraestrutura do Chatbot</Title>
      <Paragraph style={styles.subtitle}>Configure as instâncias de WhatsApp para cada empresa</Paragraph>

      <View style={styles.filterContainer}>
        <Title style={styles.filterTitle}>Filtrar por tipo:</Title>
        <SegmentedButtons
          value={tipoIntegracaoFiltro}
          onValueChange={setTipoIntegracaoFiltro}
          buttons={[
            { value: 'smartcobranca', label: 'SmartCobrança' },
            { value: 'todos', label: 'Todos' }
          ]}
          style={styles.segmentedButtons}
        />
      </View>

      {infraestruturas.length === 0 ? (
        <Card style={styles.emptyCard}>
          <Card.Content>
            <Title>Nenhuma infraestrutura cadastrada</Title>
            <Paragraph>Clique no botão + para adicionar uma nova configuração.</Paragraph>
          </Card.Content>
        </Card>
      ) : (
        <FlatList
          data={infraestruturas}
          renderItem={renderItem}
          keyExtractor={(item) => item.id?.toString() || Math.random().toString()}
          contentContainerStyle={styles.listContainer}
          key={numColumns}
          numColumns={numColumns}
          columnWrapperStyle={numColumns > 1 ? styles.columnWrapper : undefined}
        />
      )}

      <FAB
        style={[styles.fab, styles.fabEvolution]}
        icon="whatsapp"
        label="Nova Instância WhatsApp"
        onPress={openEvolutionDialog}
        color="#fff"
      />

      <Portal>
        {/* Dialog para exibir QR code */}
        <Dialog visible={qrCodeDialogVisible} onDismiss={() => setQRCodeDialogVisible(false)} style={styles.qrCodeDialog}>
          <Dialog.Title>QR Code para Sincronização</Dialog.Title>
          <Dialog.Content>
            {loadingQRCode ? (
              <View style={styles.qrCodeLoading}>
                <ActivityIndicator size="large" color={theme.colors.primary} />
                <Paragraph style={styles.qrCodeLoadingText}>Obtendo QR code...</Paragraph>
              </View>
            ) : qrCodeData ? (
              <View style={styles.qrCodeContainer}>
                <Paragraph style={styles.qrCodeInstancia}>Instância: {currentInstanceName}</Paragraph>
                <Image
                  source={{ uri: qrCodeData.base64 }}
                  style={styles.qrCodeImage}
                  resizeMode="contain"
                />
                <Paragraph style={styles.qrCodeInstructions}>
                  Abra o WhatsApp no seu celular, vá em Menu &gt; Dispositivos Conectados &gt; Conectar um dispositivo
                  e escaneie este QR code.
                </Paragraph>
              </View>
            ) : (
              <Paragraph>Não foi possível obter o QR code.</Paragraph>
            )}
          </Dialog.Content>
          <Dialog.Actions>
            {qrCodeData && (
              <Button onPress={() => handleGetQRCode(currentInstanceName)} loading={loadingQRCode} disabled={loadingQRCode}>
                Atualizar QR Code
              </Button>
            )}
            <Button onPress={() => setQRCodeDialogVisible(false)}>Fechar</Button>
          </Dialog.Actions>
        </Dialog>
        
        {/* Dialog para criar instância na Evolution API */}
        <Dialog visible={evolutionDialogVisible} onDismiss={() => setEvolutionDialogVisible(false)}>
          <Dialog.Title>Nova Instância Evolution API</Dialog.Title>
          <Dialog.Content>
            <TextInput
              label="Nome da Instância"
              value={novaInstancia.instanceName}
              onChangeText={(text) => setNovaInstancia(prev => ({ ...prev, instanceName: text }))}
              mode="outlined"
              style={styles.input}
              placeholder="Ex: EMPRESA-WHATSAPP"
            />
            
            <TextInput
              label="Número do WhatsApp"
              value={novaInstancia.numero}
              onChangeText={(text) => setNovaInstancia(prev => ({ ...prev, numero: text }))}
              mode="outlined"
              style={styles.input}
              placeholder="Ex: 5582999999999"
              keyboardType="phone-pad"
            />
            
            {/* Dropdown para selecionar empresa */}
            <View style={styles.dropdownContainer}>
              <Title style={styles.dropdownLabel}>Empresa</Title>
              <ScrollView horizontal style={styles.empresaSelector}>
                {empresas.map((empresa) => (
                  <Button 
                    key={empresa.id}
                    mode={novaInstancia.codempresa === empresa.id ? "contained" : "outlined"}
                    onPress={() => setNovaInstancia(prev => ({ ...prev, codempresa: empresa.id }))}
                    style={styles.empresaButton}
                    labelStyle={styles.empresaButtonLabel}
                  >
                    {empresa.nome}
                  </Button>
                ))}
              </ScrollView>
            </View>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setEvolutionDialogVisible(false)}>Cancelar</Button>
            <Button onPress={handleCreateEvolutionInstance} loading={creatingInstance}>Criar Instância</Button>
          </Dialog.Actions>
        </Dialog>
        
        {/* Dialog para adicionar/editar infraestrutura */}
        <Dialog visible={dialogVisible} onDismiss={() => setDialogVisible(false)}>
          <Dialog.Title>{isEditing ? 'Editar Infraestrutura' : 'Nova Infraestrutura'}</Dialog.Title>
          <Dialog.Content>
            <TextInput
              label="Nome da Instância"
              value={currentInfraestrutura.instance_name}
              onChangeText={(text) => setCurrentInfraestrutura(prev => ({ ...prev, instance_name: text }))}
              mode="outlined"
              style={styles.input}
              placeholder="Ex: WhatsApp Empresa X"
            />
            
            <TextInput
              label="Rota API"
              value={currentInfraestrutura.rotaapi}
              onChangeText={(text) => setCurrentInfraestrutura(prev => ({ ...prev, rotaapi: text }))}
              mode="outlined"
              style={styles.input}
              placeholder="Ex: /api/whatsapp"
            />
            
            <TextInput
              label="API Key da Instância"
              value={currentInfraestrutura.apikey_instance}
              onChangeText={(text) => setCurrentInfraestrutura(prev => ({ ...prev, apikey_instance: text }))}
              mode="outlined"
              style={styles.input}
              placeholder="Chave de API da instância"
            />
            
            {/* Dropdown para selecionar empresa (simplificado) */}
            <TextInput
              label="Empresa"
              value={currentInfraestrutura.codempresa.toString()}
              onChangeText={(text) => {
                const empresaId = parseInt(text);
                if (!isNaN(empresaId)) {
                  setCurrentInfraestrutura(prev => ({ ...prev, codempresa: empresaId }));
                }
              }}
              mode="outlined"
              style={styles.input}
              keyboardType="numeric"
              placeholder="ID da empresa"
            />
            
            <Title style={styles.sectionTitle}>Tipo de Integração</Title>
            <SegmentedButtons
              value={currentInfraestrutura.tipo_integracao || 'smartcobranca'}
              onValueChange={(value) => setCurrentInfraestrutura(prev => ({ ...prev, tipo_integracao: value }))}
              buttons={[
                { value: 'smartcobranca', label: 'SmartCobrança' },
                { value: 'outro', label: 'Outro' }
              ]}
              style={styles.segmentedButtons}
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDialogVisible(false)}>Cancelar</Button>
            <Button onPress={handleSave} loading={loading}>Salvar</Button>
          </Dialog.Actions>
        </Dialog>

        {/* Dialog para confirmar exclusão */}
        <Dialog visible={deleteDialogVisible} onDismiss={() => setDeleteDialogVisible(false)}>
          <Dialog.Title>Confirmar Exclusão</Dialog.Title>
          <Dialog.Content>
            <Paragraph>Tem certeza que deseja excluir a infraestrutura "{currentInfraestrutura.instance_name}"?</Paragraph>
            <Paragraph style={styles.warningText}>Esta ação não pode ser desfeita.</Paragraph>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDeleteDialogVisible(false)}>Cancelar</Button>
            <Button onPress={handleDelete} color="#F44336" loading={loading}>Excluir</Button>
          </Dialog.Actions>
        </Dialog>

        <Snackbar
          visible={!!error}
          onDismiss={() => setError(null)}
          duration={3000}
          style={{ backgroundColor: '#F44336' }}
        >
          {error}
        </Snackbar>

        <Snackbar
          visible={!!success}
          onDismiss={() => setSuccess(null)}
          duration={3000}
          style={{ backgroundColor: '#4CAF50' }}
        >
          {success}
        </Snackbar>
      </Portal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    padding: 16,
  },
  columnWrapper: {
    justifyContent: 'flex-start',
    gap: 8,
  },
  modernCard: {
    marginBottom: 8,
    backgroundColor: '#fff',
    borderRadius: 8,
    overflow: 'hidden',
    flex: 1,
    maxWidth: 400,
  },
  modernCardContent: {
    padding: 12,
  },
  modernHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  instanceTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1a1a1a',
  },
  modernDivider: {
    backgroundColor: '#e0e0e0',
    marginVertical: 6,
  },
  profileSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  avatarContainer: {
    marginRight: 10,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#e0e0e0',
  },
  avatarPlaceholder: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#e0e0e0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  profileInfo: {
    flex: 1,
  },
  profileName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: 1,
  },
  profileNumber: {
    fontSize: 12,
    color: '#666',
    marginBottom: 2,
  },
  empresaText: {
    fontSize: 11,
    color: '#999',
  },
  statusSection: {
    marginBottom: 8,
  },
  connectedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#10b981',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  notRegisteredBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f59e0b',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  disconnectedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ef4444',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusIcon: {
    margin: 0,
    marginRight: -6,
  },
  statusText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 11,
  },
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 4,
  },
  actionButton: {
    flex: 1,
    borderRadius: 12,
  },
  actionButtonLabel: {
    fontSize: 11,
    fontWeight: '600',
  },
  iconButton: {
    margin: 0,
  },
  qrCodeDialog: {
    maxWidth: 350,
    alignSelf: 'center',
  },
  qrCodeLoading: {
    padding: 20,
    alignItems: 'center',
  },
  qrCodeLoadingText: {
    marginTop: 8,
  },
  qrCodeContainer: {
    alignItems: 'center',
    padding: 10,
  },
  qrCodeInstancia: {
    fontWeight: 'bold',
    marginBottom: 10,
  },
  qrCodeImage: {
    width: 250,
    height: 250,
    marginVertical: 16,
  },
  qrCodeInstructions: {
    textAlign: 'center',
    marginTop: 10,
    fontSize: 14,
    color: '#666',
  },
  fabEvolution: {
    backgroundColor: '#25D366', // Cor do WhatsApp
  },
  dropdownContainer: {
    marginTop: 10,
    marginBottom: 10,
  },
  dropdownLabel: {
    fontSize: 16,
    marginBottom: 8,
  },
  empresaSelector: {
    flexDirection: 'row',
    maxHeight: 50,
  },
  empresaButton: {
    marginRight: 8,
    marginBottom: 8,
  },
  empresaButtonLabel: {
    fontSize: 12,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 24,
    textAlign: 'center',
    color: '#666',
  },
  sectionTitle: {
    fontSize: 16,
    marginTop: 8,
    marginBottom: 8,
  },
  filterContainer: {
    marginBottom: 16,
    backgroundColor: '#f5f5f5',
    padding: 12,
    borderRadius: 8,
  },
  filterTitle: {
    fontSize: 16,
    marginBottom: 8,
    fontWeight: 'bold',
  },
  listContainer: {
    paddingBottom: 80, // Espaço para o FAB
  },
  card: {
    marginBottom: 16,
    elevation: 4,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  badgeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  badge: {
    backgroundColor: '#2196F3',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
  },
  badgeText: {
    color: '#fff',
    fontSize: 12,
  },
  divider: {
    marginVertical: 8,
  },
  cardActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 8,
  },
  emptyCard: {
    marginTop: 20,
    backgroundColor: '#f5f5f5',
  },
  fab: {
    position: 'absolute',
    margin: 16,
    right: 0,
    bottom: 0,
  },
  input: {
    marginBottom: 12,
  },
  segmentedButtons: {
    marginBottom: 8,
  },
  warningText: {
    color: '#F44336',
    marginTop: 8,
  },
});
